import { Dispatch, FC, FormEvent, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  IconButton,
  IconButtonProps,
  InputBase,
  styled,
  Tooltip,
} from '@mui/material';

import { Icon } from '@components/base';
import useSuggestAsYouType from '@hooks/useSuggestAsYouType';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';

const StyledForm = styled('form')`
  align-items: center;
  display: flex;
  flex: 1;
`;

const StyledAutocomplete = styled(Autocomplete)`
  align-items: center;
  display: flex;
  flex-grow: 1;
` as typeof Autocomplete;

type SearchFieldProps = {
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  iconButtonSize: IconButtonProps['size'];
  queryText: string;
  setQueryText: Dispatch<SetStateAction<string>>;
};

const SearchField: FC<SearchFieldProps> = ({
  handleSubmit,
  iconButtonSize,
  queryText,
  setQueryText,
}) => {
  const [focus, setFocus] = useState(false);

  const { t } = useTranslation();
  const { isReadonly, updateQueryText } = useSearchAndFilter();

  const { suggestions, loading } = useSuggestAsYouType(queryText, {
    disabled: !focus,
    minLength: 3,
  });

  return (
    <StyledForm onSubmit={handleSubmit}>
      <StyledAutocomplete
        disableClearable={!queryText}
        disabled={isReadonly}
        disablePortal
        freeSolo
        fullWidth
        loading={loading}
        onBlur={() => setFocus(false)}
        onChange={(_, val) => (!val ? setQueryText('') : updateQueryText(val))}
        onFocus={() => setFocus(true)}
        options={suggestions}
        renderInput={({ InputProps, InputLabelProps: _InputLabelProps, ...rest }) => (
          <InputBase
            {...InputProps}
            {...rest}
            aria-label={t('search')}
            onChange={({ currentTarget: { value } }) => setQueryText(value)}
            placeholder={isReadonly ? 'No search text' : 'Search all titles'}
            sx={{ flex: 1 }}
            value={queryText}
          />
        )}
        value={queryText ?? ''}
      />

      {!isReadonly && (
        <Tooltip title={t('search')}>
          <IconButton aria-label='search' size={iconButtonSize} type='submit'>
            <Icon fontSize={iconButtonSize === 'small' ? 'small' : undefined} name='search' />
          </IconButton>
        </Tooltip>
      )}
    </StyledForm>
  );
};

export default SearchField;
